import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Cog, Loader2 } from "lucide-react";
import { signInWithEmailAndPassword } from "@firebase/auth";
import { firebaseAuth } from "@/shared/integrations/firebase/client";
import { useAuth } from "@/shared/hooks/useAuth";
import { useToast } from "@/shared/hooks/use-toast";
import { DEMO_USER_EMAIL } from "@/shared/lib/userIdUtils";

const DEMO_PASSWORD = "demo123";

const FIREBASE_ERROR_MESSAGES: Record<string, string> = {
  "auth/invalid-credential": "Invalid email or password",
  "auth/wrong-password": "Invalid email or password",
  "auth/user-not-found": "No account found with this email",
  "auth/too-many-requests": "Too many attempts. Please try again later",
  "auth/invalid-email": "Please enter a valid email address",
  "auth/network-request-failed": "Network error. Please check your connection.",
};

function getFriendlyAuthError(error: unknown): string {
  if (error && typeof error === "object" && "code" in error && typeof (error as { code: string }).code === "string") {
    const code = (error as { code: string }).code;
    return FIREBASE_ERROR_MESSAGES[code] ?? "Something went wrong. Please try again.";
  }
  return "Something went wrong. Please try again.";
}

export default function Sandbox() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { redirectToUserDashboard } = useAuth();
  const { toast } = useToast();

  const isAuthenticated = firebaseAuth?.currentUser !== null;

  useEffect(() => {
    if (isAuthenticated) return;

    let cancelled = false;

    async function signIn() {
      try {
        if (!firebaseAuth) {
          throw new Error("Firebase is not configured.");
        }

        await signInWithEmailAndPassword(firebaseAuth, DEMO_USER_EMAIL, DEMO_PASSWORD);

        if (!cancelled) {
          setTimeout(() => {
            redirectToUserDashboard();
          }, 500);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Sign-in error:", err);
          setError(getFriendlyAuthError(err));
          toast({
            title: "Sign in failed",
            description: getFriendlyAuthError(err),
            variant: "destructive",
          });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    signIn();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, redirectToUserDashboard, toast]);

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background p-4">
        <div className="flex flex-col items-center gap-6">
          <div className="bg-gradient-primary rounded-lg p-3">
            <Cog className="h-10 w-10 text-primary-foreground" />
          </div>
          <div className="text-center space-y-2">
            <p className="text-foreground font-medium">Signing in...</p>
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mx-auto" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background p-4">
      <div className="w-full max-w-md space-y-6 text-center">
        <div className="flex justify-center">
          <div className="bg-gradient-primary rounded-lg p-3">
            <Cog className="h-10 w-10 text-primary-foreground" />
          </div>
        </div>
        <div className="space-y-2">
          <h1 className="text-foreground text-xl font-semibold">Sign in failed</h1>
          {error && <p className="text-muted-foreground text-sm">{error}</p>}
        </div>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Try again
          </button>
          <Link
            to="/signin"
            className="inline-flex items-center justify-center rounded-md border border-border px-4 py-2 text-sm font-medium hover:bg-accent"
          >
            Sign in manually
          </Link>
        </div>
      </div>
    </div>
  );
}
